import {searchUrl} from '@github-ui/issue-url-helper'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {LABELS} from '@github-ui/issues-shared/labels'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {noop} from '@github-ui/noop'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {NavList, useConfirm} from '@primer/react'
import debounce from 'lodash-es/debounce'
import {useCallback, useMemo} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {useNavigationContext} from '../../contexts/NavigationContext'
import {
  useCurrentViewId,
  useDirtySearchQuery,
  useQueryEditState,
  useQueryMutations,
  useQueryViewEditFields,
  useSetCurrentPage,
  useSetViewPosition,
} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {commitRemoveUserViewMutation} from '../../mutations/remove-user-view-mutation'
import {iconToPrimerIcon} from './IconHelper'
import styles from './SavedViewItem.module.css'

const DEBOUNCE_MS = 1000

type Props = {
  id?: string
  icon?: string
  // progressively migrate to Icon
  Icon?: Icon
  color: string
  title: string
  description?: string
  query?: string
  position: number
}

function StaticIconRenderer({component}: {component?: Icon | null}) {
  if (!component) return null
  const Component = component
  return <Component />
}

export function SavedViewItem({id, icon, Icon, color, title, position, query}: Props) {
  const ResolvedIcon = useMemo(() => (icon ? iconToPrimerIcon(icon) : null), [icon])
  const IconComponent = Icon ?? ResolvedIcon
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  const setViewPosition = useSetViewPosition()
  const currentViewId = useCurrentViewId()
  const {setCanEditView, isNewView, setIsNewView, setIsEditing, dirtyViewId} = useQueryEditState()
  const setCurrentPage = useSetCurrentPage()
  const {dirtyDescription, dirtyTitle} = useQueryViewEditFields()
  const dirtySearchQuery = useDirtySearchQuery()
  const {clearSavedViewEditState} = useQueryMutations()
  const {closeNavigation} = useNavigationContext()
  const {navigateToUrl} = useAppNavigate()
  const relayEnvironment = useRelayEnvironment()

  const navigate = useNavigate()
  const confirm = useConfirm()
  const isCurrent = id === currentViewId
  const url = searchUrl({viewId: id, query})

  const scopedRepoSubmitQuery = useCallback(async () => {
    navigateToUrl(url)
    setCurrentPage(1)
  }, [navigateToUrl, url, setCurrentPage])

  const deleteDirtyView = useCallback(() => {
    if (dirtyViewId === undefined) {
      return
    }

    const payload = {
      environment: relayEnvironment,
      input: {shortcutId: dirtyViewId},
      onError: noop,
      onCompleted: noop,
    }

    commitRemoveUserViewMutation(payload)
  }, [dirtyViewId, relayEnvironment])

  const submitQuery = useCallback(async () => {
    // In a repo's index, we don't want to navigate, just update the query
    // and update the UI
    if (id === VIEW_IDS.repository) {
      return scopedRepoSubmitQuery()
    }

    // When navigating to a different saved view while the user is editing a newly created view
    if (isNewView && (dirtyTitle !== LABELS.views.defaultName || dirtySearchQuery !== '' || dirtyDescription !== '')) {
      const discardChanges = await confirm({
        title: LABELS.views.unsavedChangesTitle,
        content: LABELS.views.unsavedChangesContent,
        confirmButtonType: 'danger',
      })

      if (!discardChanges) {
        return
      }

      deleteDirtyView()
    }

    setIsEditing(false)
    setIsNewView(false)

    clearSavedViewEditState()

    setViewPosition(position)
    setCanEditView(true)
    closeNavigation()

    navigate(url)
  }, [
    id,
    isNewView,
    dirtyTitle,
    dirtySearchQuery,
    dirtyDescription,
    setIsEditing,
    setIsNewView,
    navigate,
    url,
    clearSavedViewEditState,
    setViewPosition,
    position,
    setCanEditView,
    closeNavigation,
    scopedRepoSubmitQuery,
    confirm,
    deleteDirtyView,
  ])

  const handleSelect = useCallback(
    (event: React.MouseEvent | React.KeyboardEvent<HTMLElement>) => {
      event.preventDefault()
      submitQuery()
    },
    [submitQuery],
  )

  // Debounce to prevent double-click INP spikes
  const onSelect = useMemo(() => debounce(handleSelect, DEBOUNCE_MS, {leading: true, trailing: false}), [handleSelect])

  const item = (
    <NavList.Item
      to={url}
      as={Link}
      aria-current={isCurrent ? 'page' : undefined}
      onClick={onSelect}
      className={styles.navItem}
    >
      <NavList.LeadingVisual>
        <span className={styles.icon} data-color={color.toLowerCase()}>
          <StaticIconRenderer component={IconComponent} />
        </span>
      </NavList.LeadingVisual>
      <span className={styles.truncatedItemText}>{title}</span>
    </NavList.Item>
  )

  if (visualIsExpanded) return item

  return <SidebarTooltip text={title}>{item}</SidebarTooltip>
}
